import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import { translate } from 'i18n/i18n'
import { colors } from 'settings/colors'
import type { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import type { TGetCacheUpdatesFromResponseParameters, TGetCacheUpdatesFromResponseReturn } from '../_types'
import { getPressureBadgeStyle, NO_DATA_COLORS } from './getPressureBadgeStyle'
import { getPressureBaseZIndex } from './getPressureBaseZIndex'
import { getPropertyPressureRow } from './getPropertyPressureRow'
import { Z_INDEX_NOT_APPLICABLE } from './irrigationActivityConstants'

export const getPressurePropertyItems = ({
  nowMs,
  properties,
  propertyItems,
  propertyValueType,
  response,
  selectedEmitters,
  showValues,
}: {
  nowMs: number
  properties: NonNullable<ReturnType<typeof fieldAssetStore.getState>['properties']>
  propertyItems: TGetCacheUpdatesFromResponseParameters['processedCaches']['propertyValues']['itemsWithinView']
  propertyValueType: string
  response: routes.ValuesCurrent.Response
  selectedEmitters: TEmitterType[]
  showValues: boolean
}): NonNullable<TGetCacheUpdatesFromResponseReturn['properties']>['itemsWithinViewThatNowHaveValues'] =>
  propertyItems.map((property) => {
    const id = String(property.id)

    if (!showValues) {
      return {
        id,
        value: {
          [propertyValueType]: {
            baseZIndex: Z_INDEX_NOT_APPLICABLE,
            children: property.meta.propertyName,
            getContainerCSS: () => ({ backgroundColor: colors.midnight, color: colors.white }),
            onHover: false,
          },
        },
      }
    }

    const row = getPropertyPressureRow({
      nowMs,
      propertyValues: response.properties?.[property.id],
      propertyValuesCurrent: properties[Number(property.id)]?.valuesCurrent ?? [],
      selectedEmitters,
    })

    if (!row) {
      return {
        id,
        value: {
          [propertyValueType]: {
            baseZIndex: Z_INDEX_NOT_APPLICABLE,
            children: translate.phrases.abbreviations('Not Applicable'),
            getContainerCSS: () => NO_DATA_COLORS,
            onHover: true,
          },
        },
      }
    }

    const { isRunning, reading, status } = row

    return {
      id,
      value: {
        [propertyValueType]: {
          baseZIndex: getPressureBaseZIndex({ isRunning, state: reading.state }),
          children:
            reading.state === 'NONE'
              ? translate.phrases.templates('-')
              : unitConverter.irrigationPressure(reading.psi).value(),
          getContainerCSS: () => getPressureBadgeStyle({ state: reading.state, status }),
          onHover: true,
        },
      },
    }
  })
